import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ShieldCheck, Lock, Mail, Phone, RefreshCw, LogOut } from 'lucide-react';
import { getSocket } from '../../lib/socket.js';
import { apiGet } from '../../lib/api.js';
import { useAuth } from '../../store/auth.js';
import toast from 'react-hot-toast';
import Logo from '../Logo.jsx';

// After approval a new agent lands on Phone Setup (never the dashboard), where the Producer Agreement is signed inline.
export const POST_APPROVAL_PATH = '/phone-setup';

export default function ApprovalWaitingScreen({ onApproved }) {
  const { agent, token, setSession, logout } = useAuth();
  const [checking, setChecking] = useState(false);
  const [approved, setApproved] = useState(false);
  const nav = useNavigate();
  const [managerName, setManagerName] = useState(agent?.managerName || '');
  const [manager, setManager] = useState({ email: agent?.managerEmail || '', phone: agent?.managerPhone || '' });

  // Pull the approving manager's name (not part of the login payload).
  useEffect(() => {
    let cancelled = false;
    apiGet('/auth/me').then((res) => {
      if (cancelled) return;
      if (res?.agent?.managerName) setManagerName(res.agent.managerName);
      setManager({ email: res?.agent?.managerEmail || '', phone: res?.agent?.managerPhone || '' });
    }).catch(() => {});
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    const socket = getSocket();
    if (!socket) return;

    // Both names fire on approval (legacy `agent:approved`, spec'd `team:agent-approved`);
    // react once so the toast and navigation don't double up.
    let handled = false;
    function handleApproved() {
      if (handled) return;
      handled = true;
      toast.success('Your Team Manager has approved your account! Welcome to CIVIX.', {
        icon: '🎉',
        duration: 6000,
        id: 'agent-approved',
      });
      setApproved(true);
      const updatedAgent = { ...(agent || {}), status: 'active', active: true };
      setSession(token, updatedAgent);
      if (onApproved) onApproved(updatedAgent);
      nav(POST_APPROVAL_PATH, { replace: true });
    }

    socket.on('team:agent-approved', handleApproved);
    socket.on('agent:approved', handleApproved);
    return () => {
      socket.off('team:agent-approved', handleApproved);
      socket.off('agent:approved', handleApproved);
    };
  }, [agent, token, setSession, onApproved, nav]);

  async function checkStatus() {
    setChecking(true);
    try {
      const res = await apiGet('/auth/me');
      if (res?.agent?.status === 'active' || res?.agent?.active) {
        toast.success('Your account is active!');
        setApproved(true);
        setSession(token, res.agent);
        if (onApproved) onApproved(res.agent);
        nav(POST_APPROVAL_PATH, { replace: true });
      } else {
        toast('Still pending manager approval. Hang tight!', { icon: '⏳' });
      }
    } catch (e) {
      toast.error('Could not verify status. Please try again.');
    } finally {
      setChecking(false);
    }
  }

  return (
    <div className="min-h-[80vh] flex items-center justify-center p-4">
      <div className="panel max-w-lg w-full p-8 text-center space-y-6 relative overflow-hidden border border-civix-purple/30 shadow-2xl bg-gradient-to-b from-civix-panel to-black/80">
        {/* Glow ambient */}
        <div className="absolute -top-24 left-1/2 -translate-x-1/2 w-48 h-48 bg-civix-purple/20 rounded-full blur-3xl pointer-events-none" />

        <div className="flex justify-center">
          <Logo size="md" />
        </div>

        <div className="relative mx-auto w-20 h-20" data-testid="holding-lock">
          <span className="absolute inset-0 rounded-2xl bg-civix-purple/30 animate-ping" aria-hidden="true" />
          <div className="relative w-20 h-20 rounded-2xl bg-civix-purple/15 border border-civix-purple/40 flex items-center justify-center text-civix-purpleSoft shadow-glow-purple">
            <Lock className="w-10 h-10" />
          </div>
        </div>

        <div className="space-y-2">
          <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-amber-500/15 text-amber-300 border border-amber-500/30">
            <ShieldCheck className="w-3.5 h-3.5" /> Pending Team Manager Approval
          </div>
          <h2 className="font-display text-2xl font-bold text-white tracking-tight">
            Registration Received
          </h2>
          <p className="text-white/70 text-sm leading-relaxed max-w-md mx-auto">
            Your account is currently in a holding state pending manager approval. You will gain full access to dialer, leads, and phone numbers once approved.
          </p>
        </div>

        <div className="p-4 rounded-xl bg-white/5 border border-white/10 text-left space-y-2 text-xs">
          <div className="flex items-center justify-between text-white/50">
            <span>Name:</span>
            <span className="text-white">{agent?.name || '—'}</span>
          </div>
          <div className="flex items-center justify-between text-white/50">
            <span>Registered Email:</span>
            <span className="text-white font-mono">{agent?.email}</span>
          </div>
          <div className="flex items-center justify-between text-white/50">
            <span>Account Role:</span>
            <span className="text-white capitalize">{agent?.role || 'Agent'}</span>
          </div>
          <div className="flex items-center justify-between text-white/50">
            <span>Approver:</span>
            <span className="text-amber-300 font-medium text-right">
              {managerName ? `Waiting for approval from ${managerName}` : 'Waiting for approval from your team administrator'}
            </span>
          </div>
        </div>

        {managerName && (manager.email || manager.phone) && (
          <div className="text-xs text-white/60 space-y-1" data-testid="manager-contact">
            <div className="text-white/40 uppercase tracking-wider text-[10px]">Questions? Contact {managerName}</div>
            {manager.email && <a href={`mailto:${manager.email}`} className="inline-flex items-center gap-1.5 hover:text-white"><Mail className="w-3.5 h-3.5" />{manager.email}</a>}
            {manager.phone && <div><a href={`tel:${manager.phone}`} className="inline-flex items-center gap-1.5 hover:text-white"><Phone className="w-3.5 h-3.5" />{manager.phone}</a></div>}
          </div>
        )}

        <div className="pt-2 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-center">
          <button
            onClick={checkStatus}
            disabled={checking}
            className="btn-primary inline-flex items-center justify-center gap-2 py-2.5 px-5 text-sm font-semibold"
          >
            <RefreshCw className={`w-4 h-4 ${checking ? 'animate-spin' : ''}`} />
            {checking ? 'Checking…' : 'Check Approval Status'}
          </button>
          <button
            onClick={logout}
            className="btn-ghost inline-flex items-center justify-center gap-2 py-2.5 px-4 text-xs text-white/60 hover:text-white"
          >
            <LogOut className="w-3.5 h-3.5" /> Sign Out
          </button>
        </div>

        <p className="text-[11px] text-white/40">
          This window will instantly unlock the moment your manager clicks <strong className="text-white/70">"Approve Teammate"</strong>.
        </p>
      </div>
    </div>
  );
}
